import { FormEvent, useMemo, useState } from 'react'
import { MOBILE_LOAD_ACCESS_MINIMUM_PLANS, MobileLoadHiddenFieldGroup, MobilePlan, mobilePlanDisplayName, normalizeBrokerMc } from '@numeo/types-core'
import Button from '@mui/joy/Button'
import Checkbox from '@mui/joy/Checkbox'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import { MobileLoadAccessPolicy, MobileLoadAccessPolicyInput } from 'api/mobileLoadAccess'

const GROUP_LABELS: Record<MobileLoadHiddenFieldGroup, string> = {
    [MobileLoadHiddenFieldGroup.BROKER_IDENTITY]: 'Broker identity (name, MC, DOT, location)',
    [MobileLoadHiddenFieldGroup.RATE]: 'Rate and rate per mile',
    [MobileLoadHiddenFieldGroup.REFERENCE_ID]: 'Reference ID',
    [MobileLoadHiddenFieldGroup.EXACT_ROUTE]: 'Exact route (postal codes, windows, coordinates)',
    [MobileLoadHiddenFieldGroup.CARGO_DETAILS]: 'Cargo details (weight, commodity, pieces)',
    [MobileLoadHiddenFieldGroup.TRUST_SIGNALS]: 'Trust signals (credit and fraud checks)',
}

interface Props {
    open: boolean
    policy: MobileLoadAccessPolicy | null
    saving: boolean
    error: string | null
    onClose: () => void
    onSave: (input: MobileLoadAccessPolicyInput) => Promise<void>
}

export default function MobileLoadAccessForm({ open, policy, saving, error, onClose, onSave }: Props) {
    const [mcNumber, setMcNumber] = useState(policy?.mcNumber ?? '')
    const [label, setLabel] = useState(policy?.label ?? '')
    const [minimumPlan, setMinimumPlan] = useState<MobileLoadAccessPolicyInput['minimumPlan']>(policy?.minimumPlan ?? MobilePlan.ULTRA_MOBILE)
    const [groups, setGroups] = useState<MobileLoadHiddenFieldGroup[]>(
        policy?.hiddenFieldGroups ?? [MobileLoadHiddenFieldGroup.BROKER_IDENTITY, MobileLoadHiddenFieldGroup.RATE]
    )
    const normalizedMc = useMemo(() => normalizeBrokerMc(mcNumber), [mcNumber])

    const toggleGroup = (group: MobileLoadHiddenFieldGroup): void => {
        setGroups((current) => (current.includes(group) ? current.filter((item) => item !== group) : [...current, group]))
    }

    const submit = (event: FormEvent): void => {
        event.preventDefault()
        if (!normalizedMc) return
        void onSave({ mcNumber, label: label.trim() || undefined, minimumPlan, hiddenFieldGroups: groups, enabled: policy?.enabled ?? true })
    }

    return (
        <Modal open={open} onClose={saving ? undefined : onClose}>
            <ModalDialog sx={{ width: 'min(620px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto' }}>
                <ModalClose disabled={saving} />
                <Typography level="h3">{policy ? 'Edit MC access rule' : 'Add MC access rule'}</Typography>
                <Typography level="body-sm" textColor="neutral.600">
                    Changes become active immediately. Loads remain visible, but selected details are hidden below the minimum plan.
                </Typography>
                <form onSubmit={submit}>
                    <Stack spacing={2.25} sx={{ mt: 2 }}>
                        <FormControl error={mcNumber.length > 0 && !normalizedMc} required>
                            <FormLabel>Broker MC number</FormLabel>
                            <Input autoFocus value={mcNumber} onChange={(event) => setMcNumber(event.target.value)} placeholder="MC 1234" />
                            <FormHelperText>{normalizedMc ? `Stored as ${normalizedMc}` : 'Enter an MC containing at least one digit.'}</FormHelperText>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Internal label</FormLabel>
                            <Input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Optional campaign or broker note" />
                        </FormControl>
                        <FormControl required>
                            <FormLabel>Minimum plan</FormLabel>
                            <Select value={minimumPlan} onChange={(_event, value) => value && setMinimumPlan(value)}>
                                {MOBILE_LOAD_ACCESS_MINIMUM_PLANS.map((plan) => (
                                    <Option key={plan} value={plan}>
                                        {mobilePlanDisplayName(plan)}
                                    </Option>
                                ))}
                            </Select>
                            <FormHelperText>Users on this plan or any higher plan see the full load.</FormHelperText>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Additional hidden fields</FormLabel>
                            <Stack spacing={1}>
                                {Object.values(MobileLoadHiddenFieldGroup).map((group) => (
                                    <Checkbox key={group} checked={groups.includes(group)} onChange={() => toggleGroup(group)} label={GROUP_LABELS[group]} />
                                ))}
                            </Stack>
                        </FormControl>
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            <strong>Always hidden while locked:</strong> contact details, booking links, poster contacts, comments, special requirements, and
                            raw provider details.
                        </div>
                        {error && <Typography color="danger">{error}</Typography>}
                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <Button type="button" variant="plain" color="neutral" disabled={saving} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit" loading={saving} disabled={!normalizedMc}>
                                {policy ? 'Save changes' : 'Create rule'}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </ModalDialog>
        </Modal>
    )
}

export { GROUP_LABELS }
